import { isGlobalDrawerOpenAtom } from '@/components/GlobalNavigationDrawer/lib/constants';
import { useIsGlobalNavMobile } from '@/components/GlobalNavigationDrawer/lib/hooks';
import { DrawerItemConfig } from '@/components/GlobalNavigationDrawer/lib/types';
import { ConditionalTooltip } from '@/components/utils/conditionalComponents';
import { CommandFInvisibleText } from '@/utils/CommandFInvisibleText';
import { useBooleanStateAtom } from '@/utils/booleanState';
import { ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router';

type GlobalDrawerItemContentProps = {
    item: DrawerItemConfig;
    isDrawerOpen: boolean;
    shortcutHint?: string;
    onClick?(event: MouseEvent): void;
};

const GlobalDrawerItemContent: FC<GlobalDrawerItemContentProps> = (props) => {
    const { item, isDrawerOpen, shortcutHint, onClick } = props;
    const location = useLocation();

    const isLinkSelected = useMemo(
        () =>
            item.path !== undefined &&
            (item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path)),
        [item.exact, item.path, location.pathname],
    );

    return (
        <ConditionalTooltip
            title={`${item.label}${item.subLabel ? ` (${item.subLabel})` : ''}${
                shortcutHint ? ` (${shortcutHint})` : ''
            }`}
            condition={!isDrawerOpen}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -12],
                            },
                        },
                    ],
                },
            }}
            placement={'right'}
        >
            <ListItemButton
                onClick={onClick}
                sx={[
                    {
                        py: 1, // Reduced vertical padding for more compact items
                        px: 1,
                        my: 0.5,
                        mx: 1, // Reduced horizontal margins for gap from drawer edges
                        borderRadius: 1, // Add border radius for rounded corners
                    },
                    {
                        ...(isLinkSelected
                            ? {
                                  backgroundColor: '#007A75',
                                  borderRadius: '4px',
                                  color: 'white',
                                  '& .MuiSvgIcon-root': {
                                      fill: 'white',
                                  },
                                  '&:hover': {
                                      backgroundColor: '#007A75',
                                  },
                                  '& svg > g': {
                                      fill: 'white !important',
                                  },
                              }
                            : {}),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                ]}
            >
                <ListItemIcon
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '36px', // Reduce default minWidth to make icons more compact; auto when collapsed
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    sx={{
                        margin: 0, // Remove default margins
                        overflow: 'hidden', // Prevent text overflow
                    }}
                    primary={
                        <Typography
                            noWrap
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: '500',
                            }}
                        >
                            <CommandFInvisibleText>{item.label}</CommandFInvisibleText>
                        </Typography>
                    }
                    secondary={
                        item.subLabel ? (
                            <Typography
                                variant='caption'
                                component='div'
                                noWrap
                                title={item.subLabel} // Show full text in native tooltip on hover
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.subLabel}
                            </Typography>
                        ) : null
                    }
                />
                {isDrawerOpen && shortcutHint && (
                    <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                        {shortcutHint}
                    </Typography>
                )}
            </ListItemButton>
        </ConditionalTooltip>
    );
};

type GlobalDrawerItemProps = {
    item: DrawerItemConfig;
};

export const GlobalDrawerItem: FC<GlobalDrawerItemProps> = (props) => {
    const { item } = props;

    const [isDrawerOpen, _, closeDrawer] = useBooleanStateAtom(isGlobalDrawerOpenAtom);
    const isMobile = useIsGlobalNavMobile();

    const onClick = useCallback(
        (event: MouseEvent) => {
            if (isMobile) {
                closeDrawer();
            }
            if (typeof item.onClick === 'function') {
                item.onClick(event);
            }
        },
        [closeDrawer, isMobile, item],
    );

    // No keyboard to press it on.
    const shortcutHint = isMobile ? undefined : item.shortcutHint;

    if (typeof item.path === 'undefined') {
        return (
            <GlobalDrawerItemContent
                onClick={onClick}
                item={item}
                isDrawerOpen={isDrawerOpen}
                shortcutHint={shortcutHint}
            />
        );
    }

    return (
        <Link
            to={item.path}
            onClick={onClick}
            style={{
                color: '#000000',
                textDecoration: 'none',
            }}
        >
            <GlobalDrawerItemContent
                item={item}
                isDrawerOpen={isDrawerOpen}
                shortcutHint={shortcutHint}
            />
        </Link>
    );
};
